import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  TagIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import { discountApi } from '../utils/discountApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';

const CreateEditDiscountModal = ({ isOpen, onClose, discount, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'percentage',
    value: '',
    minimumOrderAmount: '',
    maximumDiscountAmount: '',
    usageLimit: '',
    userUsageLimit: '1',
    validFrom: '',
    validUntil: '',
    applicableRestaurants: [],
    applicableUsers: [],
    excludedUsers: [],
    applicableCategories: [],
    firstTimeUserOnly: false,
    isActive: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (discount) {
      // Populate form with existing discount data
      setFormData({
        code: discount.code || '',
        name: discount.name || '',
        description: discount.description || '',
        type: discount.type || 'percentage',
        value: discount.value?.toString() || '',
        minimumOrderAmount: discount.minimumOrderAmount?.toString() || '',
        maximumDiscountAmount: discount.maximumDiscountAmount?.toString() || '',
        usageLimit: discount.usageLimit?.toString() || '',
        userUsageLimit: discount.userUsageLimit?.toString() || '1',
        validFrom: discount.validFrom ? new Date(discount.validFrom).toISOString().slice(0, 16) : '',
        validUntil: discount.validUntil ? new Date(discount.validUntil).toISOString().slice(0, 16) : '',
        applicableRestaurants: discount.applicableRestaurants?.map(r => r._id || r) || [],
        applicableUsers: discount.applicableUsers || [],
        excludedUsers: discount.excludedUsers || [],
        applicableCategories: discount.applicableCategories || [],
        firstTimeUserOnly: discount.firstTimeUserOnly || false,
        isActive: discount.isActive !== false
      });
    } else {
      // Set default values for new discount
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const nextMonth = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      setFormData(prev => ({
        ...prev,
        validFrom: now.toISOString().slice(0, 16),
        validUntil: nextMonth.toISOString().slice(0, 16)
      }));
    }
  }, [discount]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.value) newErrors.value = 'Value is required';
    if (!formData.validFrom) newErrors.validFrom = 'Start date is required';
    if (!formData.validUntil) newErrors.validUntil = 'End date is required';

    if (formData.validFrom && formData.validUntil) {
      if (new Date(formData.validFrom) >= new Date(formData.validUntil)) {
        newErrors.validUntil = 'End date must be after start date';
      }
    }

    if (formData.type === 'percentage' && (formData.value < 0 || formData.value > 100)) {
      newErrors.value = 'Percentage must be between 0 and 100';
    }

    if (formData.minimumOrderAmount && formData.minimumOrderAmount < 0) {
      newErrors.minimumOrderAmount = 'Minimum order amount cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        value: parseFloat(formData.value),
        minimumOrderAmount: formData.minimumOrderAmount ? parseFloat(formData.minimumOrderAmount) : undefined,
        maximumDiscountAmount: formData.maximumDiscountAmount ? parseFloat(formData.maximumDiscountAmount) : undefined,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : undefined,
        userUsageLimit: parseInt(formData.userUsageLimit),
        validFrom: new Date(formData.validFrom).toISOString(),
        validUntil: new Date(formData.validUntil).toISOString()
      };

      let response;
      if (discount) {
        response = await discountApi.updateDiscount(discount._id, submitData);
      } else {
        response = await discountApi.createDiscount(submitData);
      }

      if (response.success) {
        showSuccessNotification(
          discount ? 'Discount updated successfully' : 'Discount created successfully'
        );
        onSuccess();
      } else {
        showErrorNotification(response.message || 'Failed to save discount');
      }
    } catch (error) {
      console.error('Error saving discount:', error);
      showErrorNotification('Failed to save discount');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div className="flex items-center gap-3">
            <TagIcon className="w-6 h-6 text-cheese" />
            <h2 className="text-xl font-bold text-crust">
              {discount ? 'Edit Discount' : 'Create New Discount'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-olive-light" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-crust">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Discount Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => updateFormData('code', e.target.value.toUpperCase())}
                  placeholder="e.g., SAVE20"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent ${
                    errors.code ? 'border-red-500' : 'border-secondary-300'
                  }`}
                />
                {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  placeholder="e.g., 20% Off Weekend Special"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-secondary-300'
                  }`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-crust mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                placeholder="Describe this discount offer..."
                rows={3}
                className="w-full p-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent resize-none"
              />
            </div>
          </div>

          {/* Discount Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-crust">Discount Configuration</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Discount Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => updateFormData('type', e.target.value)}
                  className="w-full p-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed_amount">Fixed Amount</option>
                  <option value="free_delivery">Free Delivery</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Value *
                </label>
                <div className="relative">
                  {formData.type === 'percentage' && (
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-olive-light">%</span>
                  )}
                  {formData.type === 'fixed_amount' && (
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-olive-light">₦</span>
                  )}
                  <input
                    type="number"
                    value={formData.value}
                    onChange={(e) => updateFormData('value', e.target.value)}
                    placeholder={formData.type === 'percentage' ? '20' : '500'}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent ${
                      formData.type === 'fixed_amount' ? 'pl-8' : ''
                    } ${formData.type === 'percentage' ? 'pr-8' : ''} ${
                      errors.value ? 'border-red-500' : 'border-secondary-300'
                    }`}
                    min="0"
                    max={formData.type === 'percentage' ? '100' : undefined}
                    step={formData.type === 'percentage' ? '0.1' : '1'}
                  />
                </div>
                {errors.value && <p className="text-red-500 text-sm mt-1">{errors.value}</p>}
              </div>

              {formData.type === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium text-crust mb-2">
                    Maximum Discount (₦)
                  </label>
                  <input
                    type="number"
                    value={formData.maximumDiscountAmount}
                    onChange={(e) => updateFormData('maximumDiscountAmount', e.target.value)}
                    placeholder="500"
                    className="w-full p-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                    min="0"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Usage Limits */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-crust">Usage Limits</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Minimum Order Amount (₦)
                </label>
                <input
                  type="number"
                  value={formData.minimumOrderAmount}
                  onChange={(e) => updateFormData('minimumOrderAmount', e.target.value)}
                  placeholder="1000"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent ${
                    errors.minimumOrderAmount ? 'border-red-500' : 'border-secondary-300'
                  }`}
                  min="0"
                />
                {errors.minimumOrderAmount && <p className="text-red-500 text-sm mt-1">{errors.minimumOrderAmount}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Total Usage Limit
                </label>
                <input
                  type="number"
                  value={formData.usageLimit}
                  onChange={(e) => updateFormData('usageLimit', e.target.value)}
                  placeholder="100"
                  className="w-full p-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Per User Limit
                </label>
                <input
                  type="number"
                  value={formData.userUsageLimit}
                  onChange={(e) => updateFormData('userUsageLimit', e.target.value)}
                  placeholder="1"
                  className="w-full p-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Validity Period */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-crust">Validity Period</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Valid From *
                </label>
                <input
                  type="datetime-local"
                  value={formData.validFrom}
                  onChange={(e) => updateFormData('validFrom', e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent ${
                    errors.validFrom ? 'border-red-500' : 'border-secondary-300'
                  }`}
                />
                {errors.validFrom && <p className="text-red-500 text-sm mt-1">{errors.validFrom}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-crust mb-2">
                  Valid Until *
                </label>
                <input
                  type="datetime-local"
                  value={formData.validUntil}
                  onChange={(e) => updateFormData('validUntil', e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent ${
                    errors.validUntil ? 'border-red-500' : 'border-secondary-300'
                  }`}
                />
                {errors.validUntil && <p className="text-red-500 text-sm mt-1">{errors.validUntil}</p>}
              </div>
            </div>
          </div>

          {/* Additional Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-crust">Additional Options</h3>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.firstTimeUserOnly}
                  onChange={(e) => updateFormData('firstTimeUserOnly', e.target.checked)}
                  className="w-4 h-4 text-cheese border-secondary-300 rounded focus:ring-cheese"
                />
                <span className="text-sm text-crust">First-time users only</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => updateFormData('isActive', e.target.checked)}
                  className="w-4 h-4 text-cheese border-secondary-300 rounded focus:ring-cheese"
                />
                <span className="text-sm text-crust">Active (users can use this discount)</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-secondary-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-olive-light border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-pepperoni to-primary-800 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : discount ? 'Update Discount' : 'Create Discount'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateEditDiscountModal;